import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import type { Badge } from '../LibraryDeps'
import { TapLayer } from '../mobile'
import { useMediaSrc } from '../useMediaSrc'
import { BadgeChips } from './BadgeChips'
import { TILE_CHIPS, TILE_DOTS } from './badges'
import { notePreview } from './useVaultNotes'
import { TYPE_ICON, type VaultItem } from './vaultData'

function duration(sec: number): string {
  const s = Math.round(sec)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

interface Props {
  item: VaultItem
  selected?: boolean
  /** Shown only to people who may change the vault. */
  onToggle?: () => void
  onOpen?: (e: React.MouseEvent) => void
  /** The phone's edge-to-edge 5-column grid (design mobile Vault). */
  compact?: boolean
  /** The item's description; the tile shows its start, the viewer all of it. */
  note?: string
  /** The item's Badges, in catalog order. */
  badges?: Badge[]
  /** Hover quick actions (desktop, Vault write): open the Badge menu / the note editor at this button. */
  onBadges?: (at: HTMLElement) => void
  onEditNote?: (at: HTMLElement) => void
}

export function MediaTile({ item, selected = false, onToggle, onOpen, compact = false, note = '', badges = [], onBadges, onEditNote }: Props) {
  const { t, i18n } = useTranslation('library')
  const { ref, src, blocked } = useMediaSrc(item.small || item.thumb)
  const date = item.createdAt
    ? new Date(item.createdAt).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })
    : ''
  const label = [t(`vault.type.${item.type}`), date, badges.map((b) => b.name).join(', '), note ? notePreview(note) : '']
    .filter(Boolean)
    .join(' · ')
  const check = onToggle && (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-label={t('select')}
      className="lib-check"
      onClick={(e) => {
        e.stopPropagation()
        onToggle()
      }}
    >
      <span className="lib-check-dot">{selected && '✓'}</span>
    </button>
  )
  if (compact) {
    return (
      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        data-k={item.id}
        className={`lib-mtile ${selected ? 'is-on' : ''}`}
        role="option"
        aria-selected={selected}
        aria-label={label}
        onClick={onOpen}
      >
        <TapLayer />
        {src ? (
          <img className="lib-tile-img" src={src} alt="" loading="lazy" />
        ) : (
          <FeatherIcon name={item.type === 'audio' ? 'mic' : item.type === 'video' ? 'play-circle' : 'image'} size={item.type === 'video' ? 22 : 16} className="lib-mtile-ph" />
        )}
        <span className="lib-mtile-meta">
          <FeatherIcon name={TYPE_ICON[item.type]} size={10} />
          {item.duration > 0 && duration(item.duration)}
        </span>
        {(note || badges.length > 0) && (
          <span className="lib-mtile-marks">
            <BadgeChips badges={badges} max={TILE_DOTS} dots />
            {note && <FeatherIcon name="align-left" size={11} />}
          </span>
        )}
        {selected && <span className="lib-mtile-sel" aria-hidden="true" />}
        {check}
      </div>
    )
  }
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      data-k={item.id}
      className={`lib-tile ${selected ? 'is-on' : ''}`}
      role="option"
      aria-selected={selected}
      aria-label={label}
      onClick={onOpen}
    >
      {src ? <img className="lib-tile-img" src={src} alt="" loading="lazy" /> : <span className="lib-tile-ph">{blocked ? t('vault.noPreview') : ''}</span>}
      <span className="lib-tile-type">{t(`vault.type.${item.type}`)}</span>
      {(onBadges || onEditNote) && (
        <span className="lib-tile-quick">
          {onBadges && (
            <button
              type="button"
              className="lib-tile-quick-btn"
              aria-label={t('vault.badges.label')}
              title={t('vault.badges.label')}
              onClick={(e) => {
                e.stopPropagation()
                onBadges(e.currentTarget)
              }}
            >
              <FeatherIcon name="tag" size={13} />
            </button>
          )}
          {onEditNote && (
            <button
              type="button"
              className="lib-tile-quick-btn"
              aria-label={note ? t('vault.notes.edit') : t('vault.notes.add')}
              title={note ? t('vault.notes.edit') : t('vault.notes.add')}
              onClick={(e) => {
                e.stopPropagation()
                onEditNote(e.currentTarget)
              }}
            >
              <FeatherIcon name="edit-3" size={13} />
            </button>
          )}
        </span>
      )}
      {(note || badges.length > 0) && (
        <span className="lib-tile-note" title={note || undefined}>
          <BadgeChips badges={badges} max={TILE_CHIPS} className="lib-tile-badges" />
          {note && <span className="lib-tile-note-text">{notePreview(note)}</span>}
        </span>
      )}
      <span className="lib-tile-date">{date}</span>
      {item.duration > 0 && <span className="lib-tile-dur">{duration(item.duration)}</span>}
      {check}
    </div>
  )
}
